import React, { useState } from "react";
import AsyncSelect from "react-select/async";

const translitMap = {
  q: "й", w: "ц", e: "у", r: "к", t: "е", y: "н", u: "г", i: "ш", o: "щ", p: "з", "[": "х", "]": "ъ",
  a: "ф", s: "ы", d: "в", f: "а", g: "п", h: "р", j: "о", k: "л", l: "д", ";": "ж", "'": "э",
  z: "я", x: "ч", c: "с", v: "м", b: "и", n: "т", m: "ь", ",": "б", ".": "ю", "/": ".",
};

const transliterate = (input) => {
  return input
    .split("")
    .map((char) => translitMap[char.toLowerCase()] || char)
    .join("");
};

const normalizeOsbText = (text) => {
  return text
    .toLowerCase()
    .replace(/осб|осп|osb/g, "osb")
    .replace(/пеноплекс|пеноплэкс/g, "пеноплэкс")
    .replace(/[()]/g, "")
    .replace(/\s+/g, " ")
    .trim();
};

function DropdownTable({ id, label, options, selectedValue, onChange, onMouseEnter, onMouseLeave, onKeyDown, compact = false }) {
  const [inputValue, setInputValue] = useState("");

  // Компактный пресет под редизайн: тонкий border, аккуратная высота,
  // токенные цвета. Используется, когда лейбл колонки уже выводится снаружи.
  const compactControlStyles = {
    width: "100%",
    minHeight: "36px",
    backgroundColor: "#ffffff",
    border: "1px solid #dbe3ef",
    borderRadius: "10px",
    boxShadow: "0 1px 2px rgba(2, 2, 23, 0.05)",
    fontSize: "13.5px",
    color: "#020217",
    "&:hover": { borderColor: "#1f7fd1" },
  };

  const loadOptions = (inputValue, callback) => {
    setTimeout(() => {
      if (!inputValue) {
        callback(options.map((opt) => ({ value: opt.value, label: opt.label })));
        return;
      }

      const searchWords = inputValue
        .toLowerCase()
        .split(/\s+/)
        .filter((word) => word.length > 0);
      const transliteratedWords = searchWords.map((word) => transliterate(word));
      const normalizedInput = normalizeOsbText(inputValue.toLowerCase());
      const normalizedTransliteratedWords = transliteratedWords.map((word) => normalizeOsbText(word));

      const filteredOptions = options.filter((option) => {
        const optionLabel = normalizeOsbText(option.label.toLowerCase());

        const matchesOriginalWords = searchWords.every((word) => optionLabel.includes(normalizeOsbText(word)));
        const matchesTransliteratedWords = normalizedTransliteratedWords.every((word) => optionLabel.includes(word));
        const matchesFullInput = optionLabel.includes(normalizedInput);
        const matchesFullTransliterated =
          transliteratedWords.length === 1 && optionLabel.includes(normalizedTransliteratedWords[0]);

        return matchesOriginalWords || matchesTransliteratedWords || matchesFullInput || matchesFullTransliterated;
      });

      // Сортировка для приоритизации вариантов, начинающихся с введенного слова (русские или транслит)
      const sortedOptions = filteredOptions.sort((a, b) => {
        const aLabel = normalizeOsbText(a.label.toLowerCase());
        const bLabel = normalizeOsbText(b.label.toLowerCase());
        const aStartsWithOriginal = searchWords.some((word) => aLabel.startsWith(normalizeOsbText(word)));
        const bStartsWithOriginal = searchWords.some((word) => bLabel.startsWith(normalizeOsbText(word)));
        const aStartsWithTranslit = normalizedTransliteratedWords.some((word) => aLabel.startsWith(word));
        const bStartsWithTranslit = normalizedTransliteratedWords.some((word) => bLabel.startsWith(word));

        // Если a начинается с оригинального или транслитерированного слова, а b нет, a выше
        if ((aStartsWithOriginal || aStartsWithTranslit) && !(bStartsWithOriginal || bStartsWithTranslit)) return -1;
        // Если b начинается с оригинального или транслитерированного слова, а a нет, b выше
        if (!(aStartsWithOriginal || aStartsWithTranslit) && (bStartsWithOriginal || bStartsWithTranslit)) return 1;
        // Если оба начинаются или не начинаются, сохраняем порядок
        return 0;
      });

      callback(sortedOptions.map((opt) => ({ value: opt.value, label: opt.label })));
    }, 200);
  };

  return (
    <div style={{ maxWidth: "100%" }} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      {label && !compact && <label htmlFor={id} className="form-label fs-4">{label}</label>}
      <div onKeyDown={onKeyDown}>
        <AsyncSelect
          id={id}
          cacheOptions
          defaultOptions={options.map((opt) => ({ value: opt.value, label: opt.label }))}
          loadOptions={loadOptions}
          value={selectedValue ? { value: selectedValue, label: selectedValue } : null}
          onChange={(selectedOption) => {
            const newValue = selectedOption ? selectedOption.value : "";
            console.log("AsyncSelect onChange:", { selectedOption, newValue });
            onChange(newValue);
          }}
          onInputChange={(newValue) => {
            console.log("AsyncSelect onInputChange:", { newValue });
            setInputValue(newValue);
          }}
          inputValue={inputValue}
          className={compact ? "" : "fs-6"}
          isSearchable
          autoFocus={!compact}
          menuPortalTarget={document.body}
          placeholder={"Выбрать..."}
          styles={{
            menuPortal: (base) => ({
              ...base,
              zIndex: 9999,
            }),
            control: (base, state) => (compact ? {
              ...base,
              ...compactControlStyles,
              borderColor: state.isFocused ? "#1f7fd1" : "#dbe3ef",
              boxShadow: state.isFocused
                ? "0 0 0 3px rgba(31,127,209,0.18), 0 4px 10px -4px rgba(0,92,169,0.2)"
                : "0 1px 2px rgba(2, 2, 23, 0.05)",
              "&:hover": { borderColor: "#1f7fd1" },
            } : {
              ...base,
              width: "100%",
              minHeight: "55px",
              whiteSpace: "auto",
              pointerEvents: "auto",
              backgroundColor: "transparent",
              boxShadow: "none",
              border: "1px solid #ddd",
              "&:hover": {
                borderColor: "#aaa",
              },
            }),
            // Дропдаун-меню под токены дизайна (.dd из final-design.html).
            menu: (base) => ({
              ...base,
              width: "100%",
              maxWidth: "100%",
              whiteSpace: "normal",
              wordWrap: "break-word",
              wordBreak: "break-word",
              pointerEvents: "auto",
              ...(compact ? {
                background: "#ffffff",
                border: "1px solid rgba(2, 2, 23, 0.08)",
                borderRadius: "12px",
                boxShadow:
                  "0 1px 2px rgba(2, 2, 23, 0.04), 0 20px 40px -18px rgba(2, 2, 23, 0.30)",
                marginTop: "6px",
                overflow: "hidden",
              } : {}),
            }),
            menuList: (base) => (compact ? {
              ...base,
              padding: "6px",
              maxHeight: "320px",
            } : base),
            option: (base, state) => (compact ? {
              ...base,
              padding: "9px 12px",
              borderRadius: "8px",
              fontFamily: '"Inter", -apple-system, system-ui, "Segoe UI", Roboto, Arial, sans-serif',
              fontSize: "13.5px",
              fontWeight: state.isSelected ? 700 : 500,
              color: state.isSelected ? "#005ca9" : "#020217",
              letterSpacing: "-0.005em",
              cursor: "pointer",
              transition: "background-color 0.12s",
              backgroundColor: state.isSelected
                ? "linear-gradient(180deg, #eaf2fb, #d6e5f4)"
                : state.isFocused
                  ? "#f6faff"
                  : "transparent",
              background: state.isSelected
                ? "linear-gradient(180deg, #eaf2fb, #d6e5f4)"
                : state.isFocused
                  ? "#f6faff"
                  : "transparent",
              boxShadow: state.isSelected ? "inset 3px 0 0 #005ca9" : "none",
              "&:active": { background: "#eaf2fb" },
            } : {
              ...base,
              whiteSpace: "normal",
              wordBreak: "break-word",
              overflowWrap: "break-word",
              display: "block",
              maxWidth: "100%",
              pointerEvents: "auto",
              backgroundColor: state.isSelected ? "#f8f9fa" : "transparent",
              color: state.isSelected ? "#333" : base.color,
              "&:hover": {
                backgroundColor: "#f8f9fa",
              },
            }),
            singleValue: (base) => ({
              ...base,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "block",
              maxWidth: "100%",
              pointerEvents: "auto",
              fontFamily: compact ? '"Inter", -apple-system, system-ui, "Segoe UI", Roboto, Arial, sans-serif' : base.fontFamily,
              fontSize: compact ? "12.5px" : base.fontSize,
              fontWeight: compact ? 500 : base.fontWeight,
              color: compact ? "#020217" : base.color,
              letterSpacing: compact ? "-0.005em" : base.letterSpacing,
            }),
            placeholder: (base) => (compact ? {
              ...base,
              color: "#7a8aa3",
              fontSize: "14px",
              fontWeight: 400,
            } : base),
            input: (base) => (compact ? {
              ...base,
              color: "#020217",
              fontSize: "14px",
              fontWeight: 500,
            } : base),
            indicatorSeparator: (base) => (compact ? {
              display: "none",
            } : base),
            dropdownIndicator: (base, state) => (compact ? {
              ...base,
              color: "#4a5875",
              padding: "0 10px",
              "&:hover": { color: "#1f7fd1" },
              transition: "transform 0.18s ease, color 0.15s",
              transform: state.selectProps.menuIsOpen ? "rotate(180deg)" : "rotate(0deg)",
            } : base),
            multiValueLabel: (base) => ({
              ...base,
              whiteSpace: "normal",
              wordBreak: "break-word",
              display: "block",
              pointerEvents: "auto",
            }),
            noOptionsMessage: (base) => (compact ? {
              ...base,
              fontFamily: '"Inter", -apple-system, system-ui, "Segoe UI", Roboto, Arial, sans-serif',
              fontSize: "13px",
              color: "#7a8aa3",
              padding: "12px",
            } : base),
            loadingMessage: (base) => (compact ? {
              ...base,
              fontFamily: '"Inter", -apple-system, system-ui, "Segoe UI", Roboto, Arial, sans-serif',
              fontSize: "13px",
              color: "#7a8aa3",
              padding: "12px",
            } : base),
          }}
          onMouseEnter={(e) => {
            e.stopPropagation();
            if (onMouseEnter) onMouseEnter(e);
          }}
          onMouseLeave={(e) => {
            e.stopPropagation();
            if (onMouseLeave) onMouseLeave(e);
          }}
        />
      </div>
    </div>
  );
}

export default DropdownTable;